/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import {
  CloudWasteSettingCategorised,
  CloudWasteSettingKey,
  IntegrationPluginType,
  useIntegrationPluginsQuery,
} from "~/generated/graphql";
import { isEmpty } from "~/tools";
import {
  mapCategories,
  mapSettingsParameters,
  mapSettingsStatus,
  validateValue,
} from "./mapping";
import { Setting, UpdateSetting } from "./types";

type Error = Partial<Record<Setting["id"], string>>;

export function useFormState(initialData: CloudWasteSettingCategorised[]) {
  const [data, setData] = React.useState(() => mapCategories(initialData));
  const [error, setError] = React.useState<Error | undefined>();

  React.useEffect(() => {
    setData(mapCategories(initialData));
  }, [initialData]);

  const validateParams = React.useCallback(() => {
    const flatData = data.flatMap(({ settings }) => settings);
    return flatData.every((item) => {
      return item.parameters.every((parameter) => {
        if (
          // default empty string to -1, so it won't pass the validation
          validateValue(
            parameter.min,
            parameter.max,
            parseInt(parameter.value || "-1", 10)
          )
        ) {
          return true;
        }

        setError(
          (prevState) =>
            ({
              ...(prevState != null && prevState),
              [item.id]: `Input value must be in range (${parameter.min} - ${parameter.max})`,
            }) as Error
        );

        return false;
      });
    });
  }, [data]);

  const handlers = {
    handleSubmit: (callback: (output: UpdateSetting[]) => void) => {
      if (validateParams()) {
        callback(mapSubmitData(data));
      }
    },
    handleStatusChange: (itemId: string) => {
      setData((prevState) =>
        prevState.map(({ category, settings }) => {
          return {
            category,
            settings: mapSettingsStatus(settings, itemId),
          };
        })
      );
    },
    handleInputChange: (itemId: string, parameterId: string, value: string) => {
      setError(undefined);
      setData((prevState) =>
        prevState.map(({ category, settings }) => ({
          category,
          settings: mapSettingsParameters({
            settings,
            itemId,
            parameterId,
            value,
          }),
        }))
      );
    },
  };

  return { data, error, handlers };
}

function mapSubmitData(
  input: ReturnType<typeof useFormState>["data"]
): UpdateSetting[] {
  const flatInput = input.flatMap(({ settings }) => settings);
  return flatInput.map((item) => {
    // extract required fields for the output
    const { id, status, parameters = [] } = item;
    return {
      id,
      status,
      parameters: parameters.map((parameter) => ({
        id: parameter.id,
        value: parameter.value,
      })),
    };
  });
}

export function useTurbonomicPolicySettings<
  T extends { id: CloudWasteSettingKey },
>({ id }: T) {
  const isTurbonomicItem = id === CloudWasteSettingKey.TurbonomicAction;
  const { data, error, loading } = useIntegrationPluginsQuery({
    fetchPolicy: "network-only",
    skip: !isTurbonomicItem,
    variables: {
      filter: {
        integrationType: IntegrationPluginType.Turbonomic,
      },
    },
  });
  const disableTurbonomicSettings =
    isTurbonomicItem && isEmpty(data?.integrationPlugins ?? []);
  const turbonomicError =
    error !== undefined
      ? {
          TURBONOMIC_ACTION: "Failed to load integration plugins.",
        }
      : undefined;

  return {
    disableTurbonomicSettings,
    disabledDescription: NO_TURBONOMIC_PLUGIN_MESSAGE,
    loading,
    turbonomicError,
  };
}

const NO_TURBONOMIC_PLUGIN_MESSAGE =
  "This setting is disabled because there are no Turbonomic integration plugins added.";
